// src/tattoo/lib/shopUtils.js
//
// Shared utilities for rendering / working with `shop` records.
// Consolidated from ArtistPublicPage.jsx, components/TestLeadForm.jsx,
// and components/ArtistDirectoryBrowser.jsx where they were previously
// duplicated (and in a couple of cases re-defined inside the component
// body shadowing the outer copy).

// ---------- display helpers ----------

// Nice human location line (e.g. "Toronto, ON, Canada")
export function formatLocation(shop) {
  const parts = [shop.city, shop.region || shop.province, shop.country].filter(
    Boolean
  );
  return parts.join(", ");
}

// Normalize styles into an array regardless of how the column is stored
export function getStyleArray(shop) {
  if (Array.isArray(shop.styles)) return shop.styles;
  if (Array.isArray(shop.style_tags)) return shop.style_tags;
  if (typeof shop.styles === "string") {
    return shop.styles
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

export function formatStyles(shop) {
  return getStyleArray(shop).join(" · ");
}

// Initials for an avatar fallback (e.g. "Ted Sheppard" -> "TS")
export function getInitials(name) {
  if (!name) return "TT";
  const bits = name.trim().split(/\s+/);
  if (bits.length === 1) return bits[0].slice(0, 2).toUpperCase();
  return (bits[0][0] + bits[1][0]).toUpperCase();
}

// ---------- URL helpers ----------

export function getWebsiteUrl(shop) {
  return shop.website_url || shop.website || shop.site_url || shop.url || null;
}

export function getInstagramUrl(shop) {
  return (
    shop.instagram_url ||
    shop.instagram ||
    shop.ig_url ||
    shop.instagram_handle ||
    null
  );
}

// Build a Google Maps link, preferring an explicit URL, then a full address,
// then falling back to "shop name + rough location".
export function buildMapsUrl(shop) {
  const explicit =
    shop.google_maps_url || shop.maps_url || shop.map_url || null;
  if (explicit) return explicit;

  const addrParts = [
    shop.street || shop.address_line1 || shop.address || "",
    shop.city || "",
    shop.region || shop.province || "",
    shop.postal_code || "",
    shop.country || "",
  ].filter(Boolean);

  let query = addrParts.join(", ");

  if (!query) {
    const roughLoc = [shop.city, shop.region || shop.province, shop.country]
      .filter(Boolean)
      .join(", ");
    query = [shop.name || "", roughLoc].filter(Boolean).join(" ");
  }

  if (!query) return null;

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query
  )}`;
}

// ---------- distance helpers (optional, uses browser geolocation) ----------

export function toRad(deg) {
  return (deg * Math.PI) / 180;
}

export function getShopCoords(shop) {
  const lat =
    shop.lat ?? shop.latitude ?? shop.location_lat ?? shop.location_latitude;
  const lng =
    shop.lng ??
    shop.lon ??
    shop.longitude ??
    shop.location_lng ??
    shop.location_longitude;

  if (typeof lat === "number" && typeof lng === "number") {
    return { lat, lng };
  }
  return null;
}

export function distanceKm(a, b) {
  const R = 6371; // km
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);

  const c =
    sinDLat * sinDLat + sinDLon * sinDLon * Math.cos(lat1) * Math.cos(lat2);

  const d = 2 * Math.atan2(Math.sqrt(c), Math.sqrt(1 - c));
  return R * d;
}

export function formatDistance(userLoc, shop) {
  const coords = getShopCoords(shop);
  if (!userLoc || !coords) return null;
  try {
    const km = distanceKm(userLoc, coords);
    if (!Number.isFinite(km)) return null;
    if (km < 1) return `≈${Math.round(km * 1000)} m away`;
    return `${km.toFixed(1)} km away`;
  } catch {
    return null;
  }
}
